import { Fragment } from 'react'
import PropTypes from 'prop-types'

import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Checkbox,
    Pagination,
    createTheme,
    ThemeProvider,
    Box,
    Collapse,
} from '@mui/material'

import './AppTable.scss'

const appTableTheme = createTheme({
    palette: {
        primary: { main: '#053F48' },
    },
})

export default function AppTable({
    columns = [],
    rows = [],
    pagination = {},
    loading = false,
    rowKey = 'id',

    selectable = false,
    selectedRows = [],
    onSelectionChange,

    stickyHeader = true,

    emptyText = 'No records found.',

    onRowClick,
    expandedRowKey,
    renderExpandedRow,

    onPageChange,
    onRowsPerPageChange,

    noPagination = false,

    toolBar = null,
}) {

    const currentPage = Math.max(
        (pagination?.current_page || 1),
        1
    )

    const handlePageChange = (event, page) => {
        if (onPageChange) {
            onPageChange(page)
        }
    }

    const isSelected = (row) =>
        selectedRows.includes(row[rowKey])

    const handleSelectAll = (event) => {
        if (!onSelectionChange) return

        if (event.target.checked) {
            onSelectionChange(
                rows.map(row => row[rowKey])
            )
            return
        }

        onSelectionChange([])
    }

    const handleSelectRow = (row) => {
        if (!onSelectionChange) return

        const key = row[rowKey]

        if (selectedRows.includes(key)) {
            onSelectionChange(
                selectedRows.filter(item => item !== key)
            )
            return
        }

        onSelectionChange([
            ...selectedRows,
            key
        ])
    }

    return (
        <Paper
            className="apptable"
            elevation={0}
        >
            {toolBar && <Box className="apptable__toolbar">{toolBar}</Box>}
            <TableContainer>
                <Table stickyHeader={stickyHeader}>
                    <TableHead>
                        <TableRow>
                            {selectable && (
                                <TableCell
                                    padding="checkbox"
                                    className="apptable__head"
                                >
                                    <Checkbox
                                        checked={
                                            rows.length > 0 &&
                                            selectedRows.length === rows.length
                                        }
                                        indeterminate={
                                            selectedRows.length > 0 &&
                                            selectedRows.length < rows.length
                                        }
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                            )}
                            {columns.map(column => (

                                <TableCell
                                    key={column.field}
                                    align={column.align || 'left'}
                                    style={{
                                        width: column.width,
                                        minWidth: column.minWidth,
                                    }}
                                    className="apptable__head"
                                >

                                    {column.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        columns.length +
                                        (selectable ? 1 : 0)
                                    }
                                    align="center"
                                    className="apptable__loading"
                                >
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        )}

                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        columns.length +
                                        (selectable ? 1 : 0)
                                    }
                                    align="center"
                                    className="apptable__empty"
                                >
                                    {emptyText}
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.map(row => {
                            const key = row[rowKey]
                            const isExpanded = renderExpandedRow && expandedRowKey === key

                            return (
                                <Fragment key={key}>
                                    <TableRow
                                        hover
                                        className={`apptable__row${isExpanded ? ' apptable__row--expanded' : ''}`}
                                        onClick={() => {
                                            if (onRowClick) {
                                                onRowClick(row)
                                            }
                                        }}
                                        aria-expanded={renderExpandedRow ? isExpanded : undefined}
                                        sx={{
                                            cursor: onRowClick
                                                ? 'pointer'
                                                : 'default'
                                        }}
                                    >
                                        {selectable && (
                                            <TableCell
                                                padding="checkbox"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleSelectRow(row)
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected(row)}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map(column => {
                                            const value = row[column.field]
                                            return (
                                                <TableCell
                                                    key={column.field}
                                                    align={column.align || 'left'}
                                                >
                                                    {column.render
                                                        ? column.render(row)
                                                        : value ?? '-'
                                                    }
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                    {renderExpandedRow && (
                                        <TableRow className="apptable__expanded-row">
                                            <TableCell
                                                colSpan={columns.length + (selectable ? 1 : 0)}
                                                className="apptable__expanded-cell"
                                            >
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    {renderExpandedRow(row)}
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {
                !noPagination && (
                    <Box className="apptable__pagination-container">
                        <div className="apptable__page-size">
                            Showing {pagination?.from ?? 0} - {pagination?.to ?? 0} of {pagination?.total ?? 0} results
                        </div>
                        <ThemeProvider theme={appTableTheme}>
                            <Pagination
                                count={pagination?.last_page || 1}
                                shape="rounded"
                                page={Number(currentPage)}
                                onChange={handlePageChange}
                                color="primary"
                                next
                            />
                        </ThemeProvider>
                    </Box>
                )}
        </Paper>
    )
}

AppTable.propTypes = {
    columns: PropTypes.array,
    rows: PropTypes.array,
    pagination: PropTypes.object,
    loading: PropTypes.bool,
    rowKey: PropTypes.string,
    selectable: PropTypes.bool,
    selectedRows: PropTypes.array,
    onSelectionChange: PropTypes.func,
    stickyHeader: PropTypes.bool,
    emptyText: PropTypes.string,
    onRowClick: PropTypes.func,
    expandedRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    renderExpandedRow: PropTypes.func,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    noPagination: PropTypes.bool,
    toolBar: PropTypes.node,
}
